// Shared Bitquery theme controller (light / dark / system).
//
// Mirrors the IDE's themeStore.js so the choice is a single control across all
// *.bitquery.io apps: the preference is persisted in the `bq-theme` cookie
// scoped to `.bitquery.io`, with the IDE's legacy `bq-ide-theme` localStorage
// key kept as a read fallback. The *resolved* theme ('light' | 'dark') is
// applied to <html data-theme>; the raw preference ('system' | 'light' |
// 'dark') is mirrored to <html data-theme-pref> so the toggle can show state.
//
// The no-flash apply happens inline in shared/_head.html.erb before CSS loads;
// this module only handles live switching, OS-scheme changes, and cross-tab sync.

const COOKIE_KEY = 'bq-theme' // shared across *.bitquery.io subdomains
const STORAGE_KEY = 'bq-ide-theme' // IDE legacy per-origin mirror (read fallback)
const PREFERENCES = ['system', 'light', 'dark']

function cookieDomainAttr() {
  try {
    const host = window.location.hostname || ''
    if (host === 'bitquery.io' || host.endsWith('.bitquery.io')) return '; domain=.bitquery.io'
  } catch (e) { /* noop */ }
  return ''
}

function readCookiePreference() {
  try {
    const match = document.cookie.match(/(?:^|;\s*)bq-theme=([^;]+)/)
    const val = match && decodeURIComponent(match[1])
    if (PREFERENCES.includes(val)) return val
  } catch (e) { /* cookies unavailable */ }
  return null
}

function writeCookiePreference(pref) {
  try {
    const secure = window.location.protocol === 'https:' ? '; Secure' : ''
    // 1 year; readable by JS (no HttpOnly) so each app applies it client-side.
    document.cookie =
      `${COOKIE_KEY}=${encodeURIComponent(pref)}; path=/; max-age=31536000${cookieDomainAttr()}; SameSite=Lax${secure}`
  } catch (e) { /* noop */ }
}

function readPreference() {
  const fromCookie = readCookiePreference()
  if (fromCookie) return fromCookie
  try {
    const saved = localStorage.getItem(STORAGE_KEY)
    if (PREFERENCES.includes(saved)) return saved
  } catch (e) { /* localStorage unavailable */ }
  return 'system'
}

function systemPrefersDark() {
  try {
    return !!(window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches)
  } catch (e) {
    return false
  }
}

function resolveMode(pref) {
  if (pref === 'light' || pref === 'dark') return pref
  return systemPrefersDark() ? 'dark' : 'light'
}

// --- Google Charts theming -------------------------------------------------
// Google Charts render their own opaque white background with dark axis/legend
// text, so on a dark page they show up as light, unreadable boxes. Charts opt in
// by merging gchart.base() into their options (theme colours win) and registering
// their draw fn via gchart.onChange() so they redraw when the theme flips.
const themeChangeListeners = []

function chartColors() {
  const dark = resolveMode(readPreference()) === 'dark'
  return dark
    ? { dark: true, bg: 'transparent', text: '#c3cad6', muted: '#7e8696', grid: '#262b36', baseline: '#3b4663' }
    : { dark: false, bg: '#ffffff', text: '#1f2330', muted: '#6b7280', grid: '#e3e6eb', baseline: '#d0d7de' }
}

function gchartBase() {
  const c = chartColors()
  return {
    backgroundColor: c.bg,
    legend: { textStyle: { color: c.text } },
    titleTextStyle: { color: c.text },
    hAxis: { textStyle: { color: c.muted }, gridlines: { color: c.grid }, baselineColor: c.baseline },
    vAxis: { textStyle: { color: c.muted }, gridlines: { color: c.grid }, baselineColor: c.baseline },
  }
}

// Deep-merge `over` into `base` (over wins). Used so theme colour keys override
// a chart's hard-coded ones while leaving series/axis-titles/etc. untouched.
function bqDeepMerge(base, over) {
  const out = Object.assign({}, base)
  Object.keys(over || {}).forEach((k) => {
    const ov = over[k]
    if (ov && typeof ov === 'object' && !Array.isArray(ov) &&
        out[k] && typeof out[k] === 'object' && !Array.isArray(out[k])) {
      out[k] = bqDeepMerge(out[k], ov)
    } else {
      out[k] = ov
    }
  })
  return out
}

function gchartMerge(options) {
  return bqDeepMerge(options || {}, gchartBase())
}

let lastMode = null

function apply(pref) {
  const root = document.documentElement
  const mode = resolveMode(pref)
  const changed = lastMode !== null && lastMode !== mode
  root.setAttribute('data-theme', mode)
  root.setAttribute('data-theme-pref', pref)
  syncToggle(pref)
  if (changed) themeChangeListeners.forEach((fn) => { try { fn(mode) } catch (e) { /* noop */ } })
  lastMode = mode
}

// Icons mirror the IDE header control (bi-display / bi-sun / bi-moon) using the
// FontAwesome 5 equivalents available in this app.
const TOGGLE_META = {
  system: { icon: 'fa-desktop', name: 'System' },
  light: { icon: 'fa-sun', name: 'Light' },
  dark: { icon: 'fa-moon', name: 'Dark' },
}

// Reflect the current preference onto the header cycle button(s).
function syncToggle(pref) {
  const meta = TOGGLE_META[pref] || TOGGLE_META.system
  const next = PREFERENCES[(PREFERENCES.indexOf(pref) + 1) % PREFERENCES.length]
  const label = `Theme: ${meta.name} (click for ${TOGGLE_META[next].name})`
  document.querySelectorAll('[data-theme-toggle]').forEach((btn) => {
    // This app uses FontAwesome's SVG/JS build, which replaces our <i> with an
    // <svg> at load — so mutating an <i>'s class no longer works. Rewrite the
    // button's markup instead; FA's mutation observer re-renders the new <i>.
    btn.innerHTML = `<i class="fas ${meta.icon}" aria-hidden="true"></i>`
    btn.setAttribute('title', label)
    btn.setAttribute('aria-label', label)
  })
}

function setPreference(pref) {
  const next = PREFERENCES.includes(pref) ? pref : 'system'
  writeCookiePreference(next)
  try { localStorage.setItem(STORAGE_KEY, next) } catch (e) { /* ignore */ }
  apply(next)
}

// Cycle order matches the IDE picker: system -> light -> dark -> system.
function cyclePreference() {
  const current = readPreference()
  const idx = PREFERENCES.indexOf(current)
  setPreference(PREFERENCES[(idx + 1) % PREFERENCES.length])
}

function init() {
  apply(readPreference())

  // Track OS scheme changes while the preference is 'system'.
  if (window.matchMedia) {
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    const onChange = () => { if (readPreference() === 'system') apply('system') }
    mq.addEventListener ? mq.addEventListener('change', onChange) : mq.addListener(onChange)
  }

  // Cross-tab / cross-app sync: re-read the shared preference when another tab
  // (or another *.bitquery.io app) changes it.
  window.addEventListener('storage', (e) => { if (e.key === STORAGE_KEY) apply(readPreference()) })
  document.addEventListener('visibilitychange', () => { if (!document.hidden) apply(readPreference()) })

  // Header cycle button(s): system -> light -> dark -> system.
  document.querySelectorAll('[data-theme-toggle]').forEach((btn) => {
    btn.addEventListener('click', (e) => { e.preventDefault(); cyclePreference() })
  })
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init)
} else {
  init()
}

window.BitqueryTheme = {
  setPreference,
  cyclePreference,
  readPreference,
  gchart: {
    colors: chartColors,
    base: gchartBase,
    merge: gchartMerge,
    onChange: (fn) => { if (typeof fn === 'function') themeChangeListeners.push(fn) },
  },
}
